import { Injectable } from "@angular/core"
import { BehaviorSubject, Observable } from "rxjs"
import { Attachment } from "src/app/_interfaces/Attachment"
import { Employee } from "src/app/_interfaces/Employee"
import { HttpFunctionResult } from "src/app/_interfaces/HttpFunctionResult"
import { TicketBase } from "src/app/_interfaces/ticket"
import { TicketZeit } from "src/app/_interfaces/TicketTime"
import { WorkstepBase } from "src/app/_interfaces/workstep"
import { HttpService } from "../http.service"
import { TicketEditService } from "../ticket-edit.service"
import { TicketServiceBase } from "./ticketBase.service"
import { timeIntervalDifference } from "../../_pipes/timeDifference.Pipe"

import dayjs from '../../_utils/dayjs.config';
import { BreadcumbService } from "../breadcumb.service"
import { AddressBase } from "src/app/_interfaces/address"
import { NewTicket } from "src/app/_template/popup-content/add-ticket/add-ticket.page"
import { HttpErrorResponse, HttpResponse } from "@angular/common/http"
import { DarkmodeService } from "../darkmode.service"
import { SocketsService } from "../sockets.service"


export namespace TicketCustomer{
    @Injectable({
        providedIn: "root"
    })
    export class TicketService extends TicketServiceBase{
    
      private _ticketList: TicketBase[] = []
      private _selectedTicket: TicketBase
      private _newTicketSelected: BehaviorSubject<TicketBase> = new BehaviorSubject<TicketBase>(null)
      private _activeWorkstep: WorkstepBase
      private _employees: Employee[] = []
      private _isTime: number = 0
      private _currentAddress: AddressBase

      private _workstepLoadingCount: number = null
      constructor(
        public http: HttpService,
        public darkmode: DarkmodeService,
        private ticketEditSrv: TicketEditService,
        private timeIntervalDifference: timeIntervalDifference,
        private breadcrumbSrv: BreadcumbService,
        public socketSrv: SocketsService
      ){
        super(http, darkmode, socketSrv)
        this._newTicketSelected.subscribe((ticket: TicketBase)=>this.http.ticketId = ticket?.id || 0)
      }
  
      public get ticketList(): TicketBase[]{
        return this._ticketList
      }

      public set ticketList(list: TicketBase[]){
        this._ticketList = list
      }
  
      public get selectedTicket(): TicketBase{
        return this._selectedTicket
      }
  
      public get newTicketSelected(): Observable<TicketBase>{
        return this._newTicketSelected.asObservable()
      }

      public get activeWorkstep(): WorkstepBase{
        return this._activeWorkstep
      }

      public set activeWorkstep(ws: WorkstepBase){
        this._activeWorkstep = ws
      }

      public get employees(): Employee[]{
        return this._employees
      }

      public get isTime(): number{
        return this._isTime
      }

  public get isTimeString(): string{
    return dayjs.utc(this._isTime * 1000).format("HH:mm:ss")
  }

      public get timeDifferenceInPercent(): number{
        const percent: number = 100 / this.targetTime * this.isTime
        if(percent == Infinity) return 0
        return percent > 100 ? 100.01 : percent
      }

  public get timeDifferenceAsString(): string{
    return dayjs.utc((this.isTime - this.targetTime) * 1000).format("HH:mm:ss")
  }

      public get currentAddress(): AddressBase{
        return this._currentAddress
      }

      public get workstepLoadingCount(): number{
        return this._workstepLoadingCount
      }

      public set workstepLoadingCount(c: number){
        this._workstepLoadingCount = c
      }

      public countUpLoading(){
        this._workstepLoadingCount = this._workstepLoadingCount == null ? 1 : this._workstepLoadingCount + 1
      }

      /**
       * Läd die Adresse des angemeldeten Ansprechpartners
       */
      public async loadCurrentAddress(){
        const result: HttpFunctionResult<AddressBase> = await this.getAPIData<AddressBase>(`ticket/customer/address`)
        if(result.success) this._currentAddress = result.data
      }
      
      /**
       * Gibt alle Tickets des Ansprechpartners zurück andhand der ID in JWt
       * @returns {Promise<void>} Liste aller Tickets der Adresse des Ansprechpartners
       */
      public async getAllTicketsBase(searchText: string): Promise<void>{
        const result: HttpFunctionResult<TicketBase[]> = await this.getAPIData<TicketBase[]>(`ticket/customer?searchText=${encodeURIComponent(searchText)}`)
        if(result.success) this._ticketList = result.data
      }
  
      /**
       * Setzt ein ticket nachdem es ausgewählt wurde 
       * @param ticket Tickets welches ausgewählt wurde
       */
      public async selectTicket(ticket: TicketBase){
        this._selectedTicket = ticket
        this._newTicketSelected.next(ticket)
        this.breadcrumbSrv.breadCrumbAction(null, 0)
        if(ticket) {
          this.ticket.id = ticket.id
          this._ticketArt = {id: ticket.art, description: ""}
          this.breadcrumbSrv.addBreadcrumb({
            name: ticket.description,
            action: ()=> this.breadcrumbSrv.breadCrumbAction(async ()=> this.selectTicket(null) , 2)
          })
        }
      }

      /**
       * setzt alle zugeordneten Mitarbeiter eines Arbeitsschrittes
       */
      public async setAllWorkstepEmployees(){
        const result: HttpFunctionResult<Employee[]> = await this.getAPIData<Employee[]>(`ticket/customer/${this.activeWorkstep.id}/employees`)
        if(result.success) this._employees = result.data
        this.countUpLoading()
      }

      /**
       * Holt alle Anhänge eines Tickets
       */
      public async getAllAttachments(){
        const result: HttpFunctionResult<Attachment[]> = await this.getAPIData<Attachment[]>(`ticket/customer/attachments`)
        if(result.success) this.ticket.attachments = result.data
      }

      /**
       * Holt ist-zeiten von Arbeistsschritt und rechnet insgesamt minuten aus
       */
      public async getIsTime(){
        const result: HttpFunctionResult<TicketZeit[]> = await this.getAPIData<TicketZeit[]>(`ticket/worksteps/${this.activeWorkstep.id}/times`)
        let seconds: number = 0
        result.data.forEach(tt=> seconds += dayjs.duration(this.timeIntervalDifference.transform(tt, "HH:mm:ss", String(this.ticketArt.id)) as number).asSeconds())
        this._isTime = seconds
        this.countUpLoading()
      }
      
      /**
       * Holt Sollzeit eines Arbeitsschrittes und setzt diesen
       */
      public async getWorkstepTargetTime(){
        const result: HttpFunctionResult<number> = await this.getAPIData<number>(`ticket/customer/${this.activeWorkstep.id}/targettime`)
        if(result.success) this._targetTime = result.data
        // setTimeout(() => {
          this.countUpLoading()
    
        // }, 1000);
      }

      /**
       * Erstellt ein neues Ticket in der Datenabnk und gibt dieses zurück
       * @param {NewTicket} newTicket Ticket mit Daten welches ansgelegt werden soll
       * @returns {Promise<HttpFunctionResult<TicketBase>>} Neu erzeugtes BaseTicket
       */
        public override async createNewTicket(newTicket: NewTicket): Promise<HttpFunctionResult<TicketBase>>{
          return await this.http.POST<NewTicket, TicketBase>({
            path: "ticket/customer",
            body: newTicket
          })
        }
    }
}